import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const VideoPlayer = ({ src, title }) => (
  <div className="w-full">
    <div className="relative pb-[56.25%] h-0">
      <iframe
        src={src}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute top-0 left-0 w-full h-full rounded-lg shadow-md"
      ></iframe>
    </div>
  </div>
);

const AnimatedSection = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8 }}
  >
    {children}
  </motion.div>
);

const ImageModal = ({ isOpen, onClose, imageSrc, alt }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.5 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.5 }}
          className="relative bg-white p-2 rounded-lg max-w-3xl max-h-[90vh] overflow-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            onClick={onClose}
          >
            <X size={24} />
          </button>
          <img src={imageSrc} alt={alt} className="w-full h-auto" />
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const ProductIntroductionPage = () => {
  const [modalImage, setModalImage] = useState(null);

  const openModal = (imageSrc, alt) => {
    setModalImage({ src: imageSrc, alt });
  };

  const closeModal = () => {
    setModalImage(null);
  };

  const idealEnvironments = [
    { title: "工場・倉庫", description: "高温になりやすい大空間の冷却効率を向上" },
    { title: "オフィスビル", description: "省エネと快適な職場環境の両立を実現" },
    { title: "住宅", description: "年間を通じて快適な室内環境を維持" },
    { title: "学校・公共施設", description: "多人数が集まる空間の温度管理を効率化" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="h-16 bg-blue-800"></div>
      <header className="w-full bg-gray-200">
        <div className="relative w-full" style={{ paddingTop: '30%' }}>
          <img
            src="/images/products/maxresdefault.jpg"
            alt="はるクール製品イメージ"
            className="absolute top-0 left-0 w-full h-full object-cover"
          />
        </div>
      </header>

      <main className="container mx-auto px-4 py-12">
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-blue-50 rounded-lg shadow-lg p-8"
          >
            <h2 className="text-3xl font-bold mb-6 text-blue-800">はるクールとは</h2>
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="md:w-1/2">
                <p className="text-lg mb-4">
                  はるクールは、BXテンパル社が提供するアルミ箔を使用して開発された革新的な遮熱材です。
                  わずか0.8mmの薄さで、最大97%の熱を反射し、夏の暑さと冬の寒さから建物を守ります。
                </p>
                <h3 className="text-2xl font-semibold mb-4 text-blue-700">主な特徴</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>超薄型設計（厚さわずか0.8mm）</li>
                  <li>高い遮熱性能（最大97%の熱を反射）</li>
                  <li>長期耐久性（10年以上の性能持続）</li>
                  <li>多用途性（屋根、壁、床など様々な箇所に適用可能）</li>
                  <li>軽量で施工が容易</li>
                </ul>
              </div>
              <div className="md:w-1/2">
                <img
                  src="/images/harucool-product.jpg"
                  alt="はるクール製品の写真"
                  className="rounded-lg shadow-lg w-full h-auto max-w-md mx-auto"
                />
              </div>
            </div>
          </motion.div>
        </section>

        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-blue-50 rounded-lg shadow-lg p-8"
          >
            <h2 className="text-3xl font-bold mb-6 text-blue-800">はるクールの効果</h2>
            <div className="flex flex-col md:flex-row gap-8 justify-center">
              <div className="flex-1">
                <h3 className="text-2xl font-semibold mb-4 text-blue-700">温度低減効果</h3>
                <img
                  src="/images/harucool_ondokensho.png"
                  alt="温度低減効果の図"
                  className="rounded-lg shadow-lg w-full h-auto cursor-pointer transition-transform hover:scale-105"
                  onClick={() => openModal("/images/harucool_ondokensho.png", "温度低減効果の図")}
                />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-semibold mb-4 text-blue-700">電気量削減効果</h3>
                <img
                  src="/images/harucool_denkikensho.png"
                  alt="電気量削減効果の図"
                  className="rounded-lg shadow-lg w-full h-auto cursor-pointer transition-transform hover:scale-105"
                  onClick={() => openModal("/images/harucool_denkikensho.png", "電気量削減効果の図")}
                />
              </div>
            </div>
          </motion.div>
        </section>

        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-blue-50 rounded-lg shadow-lg p-8"
          >
            <h2 className="text-3xl font-bold mb-6 text-blue-800">はるクールの適用例</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <img
                src="/images/harucool_img05.jpg"
                alt="はるクールの適用例"
                className="rounded-lg shadow-lg w-full h-auto max-w-md mx-auto"
              />
              <img
                src="/images/building-diagram.jpg"
                alt="はるクールの建物適用例"
                className="rounded-lg shadow-lg w-full h-auto max-w-md mx-auto"
              />
            </div>
          </motion.div>
        </section>

        <section className="py-16 sm:py-24 bg-gray-50">
          <div className="container mx-auto px-4">
            <AnimatedSection>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-12 sm:mb-16 text-center text-gray-800">
                製品紹介動画
              </h2>
              <div className="flex flex-col md:flex-row justify-center items-center gap-8 max-w-4xl mx-auto">
                <VideoPlayer 
                  src="https://www.youtube.com/embed/AWDqSyJ3RSU"
                  title="はるクール紹介動画1"
                />
                <VideoPlayer 
                  src="https://www.youtube.com/embed/BTQg2jMmxrI"
                  title="はるクール紹介動画2"
                />
              </div>
            </AnimatedSection>
          </div>
        </section>

        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="bg-blue-50 rounded-lg shadow-lg p-8"
          >
            <h2 className="text-3xl font-bold mb-6 text-blue-800">遮熱材と断熱材の違い</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-semibold mb-4 text-blue-700">遮熱材（はるクール）</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>熱を反射し、室内への侵入を防ぐ</li>
                  <li>薄く、軽量で施工が容易</li>
                  <li>主に夏場の暑さ対策に効果的</li>
                  <li>屋根や外壁に使用される</li>
                </ul>
              </div>
              <div>
                <h3 className="text-2xl font-semibold mb-4 text-blue-700">断熱材</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>熱の伝導を抑え、室内温度を保持</li>
                  <li>一般的に厚みがあり、施工にスペースが必要</li>
                  <li>冬場の寒さ対策にも効果的</li>
                  <li>壁や床、天井などに使用される</li>
                </ul>
              </div>
            </div>
            <div className="mt-8">
              <h3 className="text-2xl font-semibold mb-4 text-blue-700">はるクールの優位性</h3>
              <p className="text-lg mb-4">
                はるクールは遮熱材でありながら、冬場でも室内の熱を逃がしにくい特性を持ち、
                年間を通じて快適な室内環境の維持に貢献します。その薄さと軽さから、
                従来の断熱材では難しかった場所にも容易に施工でき、
                建物全体の熱管理を効率的に行うことができます。
              </p>
            </div>
            <div className="mt-8">
              <img
                src="/images/harucool-application1.jpg"
                alt="遮熱材と断熱材の比較図"
                className="rounded-lg shadow-lg w-full h-auto max-w-2xl mx-auto"
              />
            </div>
          </motion.div>
        </section>

        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="bg-blue-50 rounded-lg shadow-lg p-8"
          >
            <h2 className="text-3xl font-bold mb-6 text-blue-800">はるクールの理想的な適用環境</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {idealEnvironments.map((env, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <h3 className="text-xl font-semibold mb-2 text-blue-700">{env.title}</h3>
                  <p className="text-gray-600">{env.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>
      </main>

      <footer className="bg-blue-800 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">はるクールで、快適な職場環境を。</h2>
          <p className="mb-4">詳細情報や導入についてのお問い合わせは、以下のリンクからお願いします。</p>
          <a href="/contact" className="text-xl hover:underline">
            お問い合わせ
          </a>
        </div>
      </footer>

      <ImageModal
        isOpen={!!modalImage}
        onClose={closeModal}
        imageSrc={modalImage?.src}
        alt={modalImage?.alt}
      />
    </div>
  );
};

export default ProductIntroductionPage;